import { useEffect, useState, useCallback } from "react";
import { Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, StatusBadge, FormDialog, Field, Loading } from "@/components/bits";
import { PasswordRequests, TempPasswordDialog } from "@/components/userBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Users() {
  const { user } = useApp();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role: "viewer" });
  const [editRow, setEditRow] = useState(null);
  const [temp, setTemp] = useState(null);
  const [editForm, setEditForm] = useState({ username: "", name: "", email: "", password: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => { api.get("/users").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/users", form);
      toast.success("User dibuat / 用户已创建");
      setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const makeTemp = async (r) => {
    if (!window.confirm(`Buat password sementara untuk ${r.username}? Password lama akan diganti.`)) return;
    try { const { data } = await api.post(`/users/${r.id}/temp-password`); setTemp(data); } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEdit = async () => {
    try {
      const payload = { username: editForm.username, name: editForm.name, email: editForm.email };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editRow.id}`, payload);
      toast.success("User diperbarui / 用户已更新");
      setEditRow(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/users/${r.id}`, { status: r.status === "Aktif" ? "Nonaktif" : "Aktif" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader id="Manajemen User & Hak Akses" zh="用户管理与权限" desc="Akun username & password untuk akses sistem."
        actions={isAdmin && <Button className="bg-sky-600 hover:bg-sky-500" onClick={() => { setForm({ name: "", username: "", email: "", password: "", role: "viewer" }); setOpen(true); }} data-testid="user-add-button"><Plus className="mr-1.5 h-4 w-4" />Tambah User</Button>} />

      {!isAdmin && (
        <div className="panel flex items-center gap-3 border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-300" data-testid="users-admin-warning">
          <ShieldAlert className="h-5 w-5 shrink-0" /> Hanya admin dapat menambah / mengubah user. 仅管理员可修改用户
        </div>
      )}

      {user?.role === "admin" && <PasswordRequests onTemp={setTemp} refreshKey={rows?.length} />}

      <div className="panel p-4" data-testid="users-panel">
        {!rows ? <Loading /> : (
          <DataTable testid="users-table" rows={rows} columns={[
            { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
            { key: "name", label: "Nama", render: (r) => <span className="font-medium">{r.name}</span> },
            { key: "username", label: "Username", render: (r) => <span className="font-mono text-xs text-amber-300" data-testid={`user-username-${r.username}`}>{r.username}</span> },
            { key: "email", label: "Email", render: (r) => <span className="font-mono text-xs text-slate-500">{r.email || "—"}</span> },
            { key: "lastLoginAt", label: "Login Terakhir", render: (r) => {
              if (!r.lastLoginAt) return <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-500" data-testid={`user-lastlogin-${r.username}`}>Belum pernah</span>;
              const days = Math.floor((Date.now() - new Date(r.lastLoginAt).getTime()) / 86400000);
              const stale = days >= 30;
              return (
                <span className="text-xs" data-testid={`user-lastlogin-${r.username}`} title={new Date(r.lastLoginAt).toLocaleString("id-ID")}>
                  <span className={stale ? "text-rose-300" : "text-slate-300"}>{days === 0 ? "Hari ini" : days === 1 ? "Kemarin" : `${days} hari lalu`}</span>
                  <span className="ml-1 font-mono text-[10px] text-slate-500">{new Date(r.lastLoginAt).toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "2-digit" })} {new Date(r.lastLoginAt).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                  {stale && <span className="ml-1 rounded border border-rose-500/40 bg-rose-500/10 px-1 text-[9px] text-rose-300">tidak aktif</span>}
                  {r.loginCount ? <span className="ml-1 font-mono text-[10px] text-slate-600">×{r.loginCount}</span> : null}
                </span>
              );
            } },
            { key: "role", label: "Role", render: (r) => <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-xs capitalize text-sky-300">{r.role}</span> },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { key: "created", label: "Dibuat", render: (r) => <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("id-ID")}</span> },
            { key: "aksi", label: "Aksi", render: (r) => isAdmin ? (
              <span className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 border-slate-700 text-xs" onClick={() => { setEditRow(r); setEditForm({ username: r.username || "", name: r.name || "", email: r.email || "", password: "" }); }} data-testid={`user-edit-${r.username}`}>Edit</Button>
                <Button size="sm" variant="outline" className="h-7 border-slate-700 text-xs" onClick={() => toggle(r)} data-testid={`user-toggle-${r.username}`}>Toggle Status</Button>
                <Button size="sm" variant="outline" className="h-7 border-amber-500/40 text-xs text-amber-300 hover:bg-amber-500/10" onClick={() => makeTemp(r)} title="Buat password sementara" data-testid={`user-temp-password-${r.username}`}>Pwd Sementara</Button>
              </span>
            ) : <span className="text-slate-600">—</span> },
          ]} />
        )}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} testid="user-dialog" title="Tambah User / 新建用户" onSubmit={submit} submitLabel="Buat User">
        <Field label="Nama"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-nama-input" /></Field>
        <Field label="Username (huruf kecil, angka, . _ -)"><Input required minLength={3} maxLength={32} pattern="[a-z0-9._\-]{3,32}" autoCapitalize="none" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} data-testid="user-username-input" /></Field>
        <Field label="Email (opsional)"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></Field>
        <Field label="Password (min. 6 karakter)"><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></Field>
        <Field label="Role">
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} data-testid="user-role-select">
            <SelectTrigger className="border-slate-800 bg-slate-900/70"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800">
              <SelectItem value="admin">admin</SelectItem>
              <SelectItem value="qc">qc</SelectItem>
              <SelectItem value="mc">mc</SelectItem>
              <SelectItem value="viewer">viewer</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </FormDialog>
    
      <TempPasswordDialog data={temp} onClose={() => setTemp(null)} />
      <FormDialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)} testid="user-edit-dialog" title={`Edit User · ${editRow?.name || ""}`} onSubmit={saveEdit} submitLabel="Simpan">
        <Field label="Username"><Input required minLength={3} maxLength={32} pattern="[a-z0-9._\-]{3,32}" autoCapitalize="none" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value.toLowerCase() })} data-testid="user-edit-username-input" /></Field>
        <Field label="Nama"><Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-nama-input" /></Field>
        <Field label="Email (opsional)"><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="user-edit-email-input" /></Field>
        <Field label="Password baru (kosongkan jika tetap)"><Input type="password" minLength={6} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="user-edit-password-input" /></Field>
      </FormDialog>
    </div>
  );
}
